// Importing necessary components and libraries from Material-UI and React
import { Box, Card, CardContent, Typography, Grid, Button, ButtonGroup, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Importing custom components
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";

// Importing styles for the component
import "./style.css";

// Importing API functions and Redux actions
import { setDashboardNumbers } from "../../apis/dashboard";
import { getDashboardNumbers } from "../../actions/dashboardAction";

// Importing types
import { DashboardDataConstants, DateFilterType, DateRange, TimeSeriesData } from "../../types/dashboard";

// Remove the separate color arrays and create a single color mapping
const metricColorMap = {
  total_rides_count: {
    main: "#4CAF50", // Green
    light: "rgba(76, 175, 80, 0.2)"
  },
  active_rides_count: {
    main: "#2196F3", // Blue
    light: "rgba(33, 150, 243, 0.2)"
  },
  completed_rides_count: {
    main: "#FF9800", // Orange
    light: "rgba(255, 152, 0, 0.2)"
  },
  cancelled_rides_by_rider_count: {
    main: "#F44336", // Red
    light: "rgba(244, 67, 54, 0.2)"
  },
  cancelled_rides_by_driver_count: {
    main: "#9C27B0", // Purple
    light: "rgba(156, 39, 176, 0.2)"
  },
  rides_without_driver_match_count: {
    main: "#3F51B5", // Indigo
    light: "rgba(63, 81, 181, 0.2)"
  },
  rejected_rides_count: {
    main: "#00BCD4", // Cyan
    light: "rgba(0, 188, 212, 0.2)"
  }
};

// Object mapping dashboard data keys to user-friendly names
const countTypes = {
  total_rides_count: "Total Rides",
  active_rides_count: "Active Rides",
  completed_rides_count: "Completed Rides",
  cancelled_rides_by_rider_count: "Cancelled Rides by Rider",
  cancelled_rides_by_driver_count: "Cancelled Rides by Driver",
  rides_without_driver_match_count: "Rides without Driver Match",
  rejected_rides_count: "Rejected Rides",
};

// Dashboard functional component
const Dashboard = () => {
  // Redux state and dispatch setup
  const { dashboardNumbers } = useSelector((state: any) => state.dashboardReducer);
  const dispatch = useDispatch();

  // Function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  // Function to get date string in YYYY-MM-DD format
  const getDateString = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  // Local state to manage dashboard data
  const [dashboardData, setDashboardData] = useState(dashboardNumbers);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: getCurrentDate(),
    endDate: getCurrentDate(),
    filterType: 'daily'
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [dateFilter, setDateFilter] = useState('daily');

  // Function to handle date filter change
  const handleDateFilterChange = (filterType: DateFilterType) => {
    const today = new Date();
    let startDate = new Date();
    
    switch (filterType) {
      case 'daily':
        startDate = today;
        break;
      case 'weekly':
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        break;
      case 'custom':
        setShowCustomDatePicker(true);
        setDateFilter('custom'); // Set filter immediately when custom is clicked
        return;
    }
    
    setShowCustomDatePicker(false);
    const newDateRange = {
      startDate: getDateString(startDate),
      endDate: getDateString(today),
      filterType
    };
    setDateRange(newDateRange);
    setDateFilter(filterType);
    getData(newDateRange);
  };

  // Function to handle custom date apply
  const handleCustomDateApply = () => {
    setShowCustomDatePicker(false);
    const newDateRange = {
      ...dateRange,
      filterType: 'custom' as DateFilterType
    };
    setDateRange(newDateRange);
    setDateFilter('custom');
    getData(newDateRange);
  };

  // Function to fetch and update dashboard data
  const getData = async (dateRangeParams?: DateRange) => {
    const data = await setDashboardNumbers(dateRangeParams || dateRange);
    dispatch(getDashboardNumbers(data));
    setDashboardData(data);
  };

  // useEffect hook to fetch data on component mount
  useEffect(() => {
    getData();
  }, []);

  // JSX structure for the Dashboard component
  return (
    <Box className="mainContainer">
      {/* Side panel for navigation */}
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>

      {/* Main dashboard container */}
      <Box className="dashboardContainer">
        {/* Top navigation bar */}
        <TopBar />

        {/* Content area */}
        <Box style={{ width: "100%", padding: "20px", marginTop: "10%" }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5%' }}>
            <h2>Hi, Welcome back 👋</h2>
            
            {/* Date filter controls */}
            <Box>
              <ButtonGroup 
                variant="text" 
                aria-label="date filter buttons"
                sx={{
                  backgroundColor: 'transparent',
                  '& .MuiButton-root': {
                    mx: 1,
                    px: 3,
                    py: 1,
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                  '& .MuiButtonGroup-grouped': {
                    border: 'none',
                    '&:not(:last-of-type)': {
                      border: 'none',
                    },
                  },
                }}
              >
                <Button 
                  onClick={() => handleDateFilterChange('daily')}
                  variant={dateFilter === 'daily' ? 'contained' : 'text'}
                >
                  Daily
                </Button>
                <Button 
                  onClick={() => handleDateFilterChange('weekly')}
                  variant={dateFilter === 'weekly' ? 'contained' : 'text'}
                >
                  Weekly
                </Button>
                <Button 
                  onClick={() => handleDateFilterChange('monthly')}
                  variant={dateFilter === 'monthly' ? 'contained' : 'text'}
                >
                  Monthly
                </Button>
                <Button 
                  onClick={() => handleDateFilterChange('custom')}
                  variant={dateFilter === 'custom' ? 'contained' : 'text'}
                >
                  Custom
                </Button>
              </ButtonGroup>
              
              {showCustomDatePicker && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <DatePicker
                      label="Start Date"
                      value={new Date(dateRange.startDate)}
                      maxDate={new Date()}
                      onChange={(newValue) => {
                        if (newValue) {
                          setDateRange(prev => ({
                            ...prev,
                            startDate: getDateString(newValue),
                            filterType: 'custom'
                          }));
                        }
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={new Date(dateRange.endDate)}
                      maxDate={new Date()}
                      onChange={(newValue) => {
                        if (newValue) {
                          setDateRange(prev => ({
                            ...prev,
                            endDate: getDateString(newValue),
                            filterType: 'custom'
                          }));
                        }
                      }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleCustomDateApply}
                    >
                      Apply
                    </Button>
                  </Box>
                </LocalizationProvider>
              )}
            </Box>
          </Box>

          {/* Bar Chart */}
          {dashboardData.timeSeries && dashboardData.timeSeries.length > 0 && (
            <Box sx={{ mt: 4, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Rides Over Time</Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dashboardData.timeSeries}
                  margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {Object.keys(countTypes).map((key) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={countTypes[key as keyof DashboardDataConstants]}
                      fill={metricColorMap[key as keyof typeof metricColorMap].main}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Grid layout for displaying dashboard data */}
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }} style={{ marginTop: '70px' }}>All Time Ride Statistics</Typography>
          <Grid container spacing={{ xs: 2, md: 2 }} columns={{ xs: 4, sm: 8, md: 12 }} style={{ marginTop: '10px' }}>
            {/* Mapping through dashboardData.current keys to render cards */}
            {Object.keys(dashboardData.current || {}).map((key) => (
              <Grid item xs={2} sm={4} md={3} key={key}>
                <Card
                  className="cardContainer"
                  style={{
                    backgroundColor: metricColorMap[key as keyof typeof metricColorMap].light,
                  }}
                >
                  <CardContent>
                    <Typography className="cardTitle" style={{ color: metricColorMap[key as keyof typeof metricColorMap].main }}>
                      {countTypes[key as keyof DashboardDataConstants]}
                    </Typography>
                    <Typography className="cardSubtitle">{dashboardData.current[key]}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

// Exporting the Dashboard component
export default Dashboard;
